import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Dashboard,
  Inventory,
  Assignment,
  Warning,
  FindInPage,
  LocalShipping,
  Inventory2,
  Build,
  CalendarMonth,
  AccountTree,
  AdminPanelSettings,
  Person,
  Help,
  Login,
  AppRegistration,
  PendingActions,
  PhoneAndroid,
} from '@mui/icons-material';
import { PrototypeAuthProvider, usePrototypeAuth } from './PrototypeAuthContext';

const DRAWER_WIDTH = 260;

const navSections = [
  { label: 'Dashboard', path: 'dashboard', icon: <Dashboard /> },
  { label: 'Incoming Deliveries', path: 'incoming-deliveries', icon: <LocalShipping /> },
  { label: 'Incoming Stock', path: 'incoming-deliveries/stock', icon: <Inventory2 /> },
  { label: 'Production Orders', path: 'production-orders', icon: <Build /> },
  { label: 'Production Planning', path: 'production-planning', icon: <CalendarMonth /> },
  { label: 'Pallets', path: 'pallets', icon: <Inventory /> },
  { label: 'Audits', path: 'audits', icon: <Assignment /> },
  { label: 'Recalls', path: 'recalls', icon: <Warning /> },
  { label: 'Trace Test Auto', path: 'trace-test-auto', icon: <FindInPage /> },
  { label: 'Supplier Documents', path: 'supplier-documents', icon: <LocalShipping /> },
  { label: 'Production Recipes', path: 'production-recipes', icon: <Build /> },
  { label: 'Shopfloor', path: 'shopfloor', icon: <Inventory /> },
  { label: 'Receive Goods', path: 'receive-goods', icon: <LocalShipping /> },
  { label: 'Production Start', path: 'production-start', icon: <Build /> },
  { label: 'Stock', path: 'stock', icon: <Inventory2 /> },
  { label: 'Processes', path: 'processes', icon: <AccountTree /> },
];

const adminSection = [
  { label: 'Master Data', path: 'administration/master-data', icon: <AdminPanelSettings /> },
  { label: 'Users', path: 'administration/users', icon: <Person /> },
];

const authSection = [
  { label: 'Login', path: 'login', icon: <Login /> },
  { label: 'Register', path: 'register', icon: <AppRegistration /> },
  { label: 'Pending Approval', path: 'pending', icon: <PendingActions /> },
  { label: 'Operator Login', path: 'operator/login', icon: <PhoneAndroid /> },
];

const otherSection = [
  { label: 'Profile', path: 'profile', icon: <Person /> },
  { label: 'Help', path: 'help', icon: <Help /> },
];

function NavLink({ to, icon, label, isActive }) {
  const theme = useTheme();
  return (
    <ListItemButton
      component={Link}
      to={to}
      selected={isActive}
      sx={{
        borderRadius: '10px',
        mb: 0.25,
        '&.Mui-selected': {
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
          color: theme.palette.primary.main,
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.18) },
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{icon}</ListItemIcon>
      <ListItemText primary={label} primaryTypographyProps={{ fontSize: '0.9rem' }} />
    </ListItemButton>
  );
}

const PUBLIC_PATHS = ['login', 'register', 'pending', 'operator/login'];

function PrototypeLayoutInner() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = usePrototypeAuth();
  const [open, setOpen] = useState(true);

  const basePath = '/prototype';
  const currentPath = location.pathname.replace(basePath, '').replace(/^\//, '') || 'dashboard';
  const isPublicPath = PUBLIC_PATHS.some((p) => currentPath === p || currentPath.startsWith(p + '/'));

  if (!isAuthenticated && !isPublicPath) {
    return <Navigate to="/prototype/login" replace />;
  }
  if (!isAuthenticated && isPublicPath) {
    return <Outlet />;
  }

  const isActive = (path) => currentPath === path || currentPath.startsWith(path + '/');

  const handleLogout = () => {
    logout();
    navigate('/prototype/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: open ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
          ml: open ? `${DRAWER_WIDTH}px` : 0,
          transition: theme.transitions.create(['margin', 'width'], { duration: theme.transitions.duration.enteringScreen }),
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton color="inherit" onClick={() => setOpen(!open)} edge="start" sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="span" sx={{ flexGrow: 1 }}>
            Trace — Interactive Prototype
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
            Every action is clickable • No dead ends
          </Typography>
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={handleLogout} size="small">
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        sx={{
          width: open ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            top: 0,
            left: 0,
            mt: '64px',
            height: 'calc(100vh - 64px)',
            borderRight: `1px solid ${theme.palette.divider}`,
            transition: theme.transitions.create('width', { duration: theme.transitions.duration.enteringScreen }),
            overflowX: 'hidden',
          },
        }}
      >
        <Box sx={{ overflow: 'auto', py: 1, px: 1 }}>
          <Typography variant="overline" sx={{ px: 2, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600 }}>
            Main
          </Typography>
          <List dense>
            {navSections.map((item) => (
              <NavLink key={item.path} to={`${basePath}/${item.path}`} icon={item.icon} label={item.label} isActive={isActive(item.path)} />
            ))}
          </List>
          <Divider sx={{ my: 1 }} />
          <Typography variant="overline" sx={{ px: 2, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600 }}>
            Administration
          </Typography>
          <List dense>
            {adminSection.map((item) => (
              <NavLink key={item.path} to={`${basePath}/${item.path}`} icon={item.icon} label={item.label} isActive={isActive(item.path)} />
            ))}
          </List>
          <Divider sx={{ my: 1 }} />
          <Typography variant="overline" sx={{ px: 2, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600 }}>
            Auth & Account
          </Typography>
          <List dense>
            {authSection.map((item) => (
              <NavLink key={item.path} to={`${basePath}/${item.path}`} icon={item.icon} label={item.label} isActive={isActive(item.path)} />
            ))}
            {otherSection.map((item) => (
              <NavLink key={item.path} to={`${basePath}/${item.path}`} icon={item.icon} label={item.label} isActive={isActive(item.path)} />
            ))}
          </List>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: '64px',
          ml: open ? 0 : 0,
          width: open ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
          transition: theme.transitions.create(['margin', 'width'], { duration: theme.transitions.duration.enteringScreen }),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export default function PrototypeLayout() {
  return (
    <PrototypeAuthProvider>
      <PrototypeLayoutInner />
    </PrototypeAuthProvider>
  );
}
